import type { GetUserDataResult } from "@/lib/types";

export type TourStatus = "not_started" | "completed" | "skipped";

const TOUR_STATUS_STORAGE_KEY = "wiswiz_tour_status";
const TOUR_PENDING_SYNC_KEY = "wiswiz_tour_pending_sync";
const LEGACY_GUIDED_COMPLETE_KEY = "wiswiz_guided_tour_complete";
const LEGACY_DEMO_COMPLETE_KEY = "wiswiz_demo_tour_complete";

export function isDismissedTourStatus(status: TourStatus | null | undefined) {
  return status === "completed" || status === "skipped";
}

function normalizeTourStatus(value: string | null): TourStatus | null {
  if (value === "not_started" || value === "completed" || value === "skipped") {
    return value;
  }
  return null;
}

export function readLocalTourStatus(): TourStatus {
  try {
    const explicit = normalizeTourStatus(
      localStorage.getItem(TOUR_STATUS_STORAGE_KEY)
    );
    if (explicit && isDismissedTourStatus(explicit)) return explicit;

    if (
      localStorage.getItem(LEGACY_GUIDED_COMPLETE_KEY) === "true" ||
      localStorage.getItem(LEGACY_DEMO_COMPLETE_KEY) === "true"
    ) {
      return "completed";
    }

    return explicit ?? "not_started";
  } catch {
    return "not_started";
  }
}

export function writeLocalTourStatus(
  status: TourStatus,
  options?: { pendingSync?: boolean }
) {
  try {
    localStorage.setItem(TOUR_STATUS_STORAGE_KEY, status);
    if (isDismissedTourStatus(status)) {
      // Keep old builds and old tests from re-showing either branch.
      localStorage.setItem(LEGACY_GUIDED_COMPLETE_KEY, "true");
      localStorage.setItem(LEGACY_DEMO_COMPLETE_KEY, "true");
      if (options?.pendingSync) {
        localStorage.setItem(TOUR_PENDING_SYNC_KEY, "true");
      } else {
        localStorage.removeItem(TOUR_PENDING_SYNC_KEY);
      }
    } else {
      localStorage.removeItem(LEGACY_GUIDED_COMPLETE_KEY);
      localStorage.removeItem(LEGACY_DEMO_COMPLETE_KEY);
      localStorage.removeItem(TOUR_PENDING_SYNC_KEY);
    }
  } catch {}
}

export function clearLocalTourPendingSync() {
  try {
    localStorage.removeItem(TOUR_PENDING_SYNC_KEY);
  } catch {}
}

export function shouldSyncLocalTourStatus(
  userData: GetUserDataResult | null | undefined
) {
  if (
    !userData ||
    userData.tour_status === undefined ||
    isDismissedTourStatus(userData.tour_status)
  ) {
    return false;
  }
  const localStatus = readLocalTourStatus();
  if (!isDismissedTourStatus(localStatus)) return false;

  try {
    return localStorage.getItem(TOUR_PENDING_SYNC_KEY) === "true";
  } catch {
    return false;
  }
}

export function getEffectiveTourStatus(
  userData: GetUserDataResult | null | undefined
) {
  if (isDismissedTourStatus(userData?.tour_status))
    return userData!.tour_status;
  const localStatus = readLocalTourStatus();
  if (isDismissedTourStatus(localStatus)) return localStatus;
  return userData?.tour_status ?? "not_started";
}
